import type { FC } from 'react'
import { useCallback } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Typography } from '@mui/material'
import type { ClaudeSession, UserInputDraft } from '@/features/events/conversation/state/types'
import { useUserAnswerDraftEntries } from '@/features/events/conversation/drafts'
import { UserAnswerDraftItem, UserInputDraftItem } from '@/features/events/pages/shared/components'

interface SessionDraftsOrchestratorProps {
  readonly session: ClaudeSession
  readonly onDraftSelect: (draft: UserInputDraft, session: ClaudeSession) => void
  readonly onAnswerDraftSelect: (questionHeader: string, session: ClaudeSession) => void
}

export const SessionDraftsOrchestrator: FC<SessionDraftsOrchestratorProps> = ({
  session,
  onDraftSelect,
  onAnswerDraftSelect,
}) => {
  const userInputDrafts = session.runtime.memory.userInputDrafts
  const userAnswerDraftEntries = useUserAnswerDraftEntries(session.runtime.memory.userAnswersDraft)

  const hasDrafts = userInputDrafts.length > 0 || userAnswerDraftEntries.length > 0

  const handleAnswerDraftClick = useCallback(
    (questionHeader: string) => onAnswerDraftSelect(questionHeader, session),
    [onAnswerDraftSelect, session],
  )

  if (!hasDrafts) {
    return null
  }

  return (
    <Box>
      {userInputDrafts.map((draft) => (
        <UserInputDraftItem
          key={draft.id}
          draft={draft}
          session={session}
          onDraftClick={onDraftSelect}
        />
      ))}
      {userAnswerDraftEntries.length > 0 && (
        <Box sx={answersSectionSx}>
          <Typography sx={answersHeaderSx}>User answers</Typography>
          {userAnswerDraftEntries.map(({ header, answer, index }) => (
            <UserAnswerDraftItem
              key={header}
              questionHeader={header}
              questionIndex={index}
              answerDraft={answer}
              onAnswerDraftClick={handleAnswerDraftClick}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

const answersSectionSx: SxProps<Theme> = {
  mt: 1.5,
}

const answersHeaderSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'warning.main',
  pl: 2,
  mb: 0.5,
}
